import React, { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { 
  Calendar, 
  Users, 
  TrendingUp, 
  AlertCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  CreditCard,
  Building2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Euro,
  Gift,
  CalendarClock,
  Target,
  Wallet,
  Download,
  Eye,
  Play,
  MousePointerClick,
  Search,
  Instagram,
  Facebook,
  MessageCircle,
  Linkedin,
  Youtube,
  Twitter,
  Music2,
  Mail,
  Globe,
  Link2
} from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import SmartPageOnboardingWizard from "../components/SmartPageOnboardingWizard";

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteStats, setSiteStats] = useState(null);
  const [sitePeriod, setSitePeriod] = useState("all");
  const [trafficSources, setTrafficSources] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/analytics/dashboard`, {
          withCredentials: true
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
        setError(error.response?.data?.detail || "Errore nel caricamento");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [user]);

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    const fetchSiteStats = async () => {
      try {
        const res = await axios.get(`${API}/analytics/site-stats`, {
          params: { period: sitePeriod },
          withCredentials: true,
        });
        setSiteStats(res.data);
      } catch { /* silent */ }
    };
    const fetchTrafficSources = async () => {
      try {
        const res = await axios.get(`${API}/analytics/traffic-sources`, {
          params: { period: sitePeriod },
          withCredentials: true,
        });
        setTrafficSources(res.data);
      } catch { /* silent */ }
    };
    fetchSiteStats();
    fetchTrafficSources();
  }, [user, sitePeriod]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === "it" ? "it-IT" : "en-US", {
      style: "currency",
      currency: "EUR"
    }).format(amount);
  };

  const TRAFFIC_SOURCE_META = {
    google:      { label: "Google",     icon: Search,        color: "text-blue-500",    bar: "bg-blue-500"    },
    instagram:   { label: "Instagram",  icon: Instagram,     color: "text-pink-500",    bar: "bg-pink-500"    },
    facebook:    { label: "Facebook",   icon: Facebook,      color: "text-[#1877F2]",   bar: "bg-[#1877F2]"   },
    whatsapp:    { label: "WhatsApp",   icon: MessageCircle, color: "text-emerald-500", bar: "bg-emerald-500" },
    tiktok:      { label: "TikTok",     icon: Music2,        color: "text-slate-900 dark:text-slate-100", bar: "bg-slate-900 dark:bg-slate-100" },
    linkedin:    { label: "LinkedIn",   icon: Linkedin,      color: "text-[#0A66C2]",   bar: "bg-[#0A66C2]"   },
    twitter:     { label: "X / Twitter",icon: Twitter,       color: "text-sky-500",     bar: "bg-sky-500"     },
    youtube:     { label: "YouTube",    icon: Youtube,       color: "text-red-500",     bar: "bg-red-500"     },
    bing:        { label: "Bing",       icon: Search,        color: "text-teal-500",    bar: "bg-teal-500"    },
    duckduckgo:  { label: "DuckDuckGo", icon: Search,        color: "text-orange-500",  bar: "bg-orange-500"  },
    telegram:    { label: "Telegram",   icon: MessageCircle, color: "text-cyan-500",    bar: "bg-cyan-500"    },
    email:       { label: "Email",      icon: Mail,          color: "text-amber-500",   bar: "bg-amber-500"   },
    direct:      { label: language === "it" ? "Diretto" : "Direct", icon: Link2, color: "text-violet-500",  bar: "bg-violet-500"  },
    other:       { label: language === "it" ? "Altro" : "Other",    icon: Globe, color: "text-slate-500",   bar: "bg-slate-500"   },
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  // Super Admin Dashboard
  if (user?.role === "super_admin") {
    return (
      <AdminLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-serif font-medium">{t("welcome_back")}, {user?.name}</h1>
            <p className="text-muted-foreground mt-1">
              {language === "it" ? "Panoramica della piattaforma" : "Platform overview"}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="stats-card bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-200 dark:border-violet-800" data-testid="stat-total-tenants">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {language === "it" ? "Clienti Totali" : "Total Clients"}
                  </p>
                  <p className="text-2xl font-mono font-bold mt-1">{analytics?.total_tenants || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-violet-500" />
                </div>
              </div>
            </div>

            <div className="stats-card" data-testid="stat-active-subscriptions">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {language === "it" ? "Abbonamenti Attivi" : "Active Subscriptions"}
                  </p>
                  <p className="text-2xl font-mono font-bold mt-1 text-emerald-600">{analytics?.active_subscriptions || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </div>

            <div className="stats-card" data-testid="stat-trial-subscriptions">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {language === "it" ? "In Prova" : "Trial"}
                  </p>
                  <p className="text-2xl font-mono font-bold mt-1 text-amber-600">{analytics?.trial_subscriptions || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </div>

            <div className="stats-card" data-testid="stat-free-subscriptions">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {language === "it" ? "Gratis" : "Free"}
                  </p>
                  <p className="text-2xl font-mono font-bold mt-1 text-pink-600">{analytics?.free_subscriptions || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-pink-500" />
                </div>
              </div>
            </div>

            <div className="stats-card" data-testid="stat-total-appointments">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    {language === "it" ? "Prenotazioni" : "Appointments"}
                  </p>
                  <p className="text-2xl font-mono font-bold mt-1">{analytics?.total_appointments || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Site Analytics Section */}
          <div className="bg-card rounded-2xl border p-6" data-testid="site-analytics-section">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Eye className="w-5 h-5 text-sky-500" />
                {language === "it" ? "Traffico Sito" : "Site Traffic"}
              </h2>
              <div className="flex items-center gap-1.5 bg-muted rounded-full p-1">
                {[
                  { key: "7d", label: "7g" },
                  { key: "30d", label: "30g" },
                  { key: "90d", label: "90g" },
                  { key: "all", label: language === "it" ? "Tutto" : "All" },
                ].map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setSitePeriod(p.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      sitePeriod === p.key
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`site-period-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800" data-testid="stat-landing-views">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-sky-500" />
                  <p className="text-xs text-sky-700 dark:text-sky-400 font-medium">
                    {language === "it" ? "Visite Landing Page" : "Landing Page Views"}
                  </p>
                </div>
                <p className="text-2xl font-mono font-bold text-sky-600">{siteStats?.landing_views ?? "—"}</p>
              </div>
              <div className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800" data-testid="stat-demo-views">
                <div className="flex items-center gap-2 mb-1">
                  <MousePointerClick className="w-4 h-4 text-indigo-500" />
                  <p className="text-xs text-indigo-700 dark:text-indigo-400 font-medium">
                    {language === "it" ? "Visite Pagina Demo" : "Demo Page Views"}
                  </p>
                </div>
                <p className="text-2xl font-mono font-bold text-indigo-600">{siteStats?.demo_views ?? "—"}</p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800" data-testid="stat-demo-started">
                <div className="flex items-center gap-2 mb-1">
                  <Play className="w-4 h-4 text-emerald-500" />
                  <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                    {language === "it" ? "Demo Avviate" : "Demos Started"}
                  </p>
                </div>
                <p className="text-2xl font-mono font-bold text-emerald-600">{siteStats?.demo_started ?? "—"}</p>
              </div>
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="bg-card rounded-2xl border p-6" data-testid="traffic-sources-section">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-5">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Globe className="w-5 h-5 text-violet-500" />
                  {language === "it" ? "Sorgenti Traffico" : "Traffic Sources"}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it"
                    ? "Da dove arrivano i visitatori della landing page"
                    : "Where landing page visitors come from"}
                  {trafficSources?.total > 0 && (
                    <span className="ml-2 font-medium text-foreground">
                      · {trafficSources.total} {language === "it" ? "visite" : "visits"}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {!trafficSources ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground text-sm" data-testid="traffic-sources-loading">
                <div className="animate-spin w-5 h-5 border-2 border-accent border-t-transparent rounded-full mr-2"></div>
                {language === "it" ? "Caricamento…" : "Loading…"}
              </div>
            ) : trafficSources.sources.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm" data-testid="traffic-sources-empty">
                {language === "it"
                  ? "Nessuna visita registrata nel periodo selezionato."
                  : "No visits recorded in the selected period."}
              </div>
            ) : (
              <div className="space-y-3" data-testid="traffic-sources-list">
                {trafficSources.sources.map((s) => {
                  const meta = TRAFFIC_SOURCE_META[s.source] || TRAFFIC_SOURCE_META.other;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={s.source}
                      className="flex items-center gap-3"
                      data-testid={`traffic-source-${s.source}`}
                    >
                      <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="text-sm font-medium truncate">{meta.label}</span>
                          <span className="text-xs text-muted-foreground font-mono shrink-0">
                            {s.count} · {s.percentage}%
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full ${meta.bar} rounded-full transition-all`}
                            style={{ width: `${Math.max(s.percentage, 2)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Private Platform Analytics - Revenue */}
          <div className="bg-card rounded-2xl border p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" />
                {language === "it" ? "Entrate Piattaforma" : "Platform Revenue"}
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  ({language === "it" ? "Private" : "Private"})
                </span>
              </h2>
              
              {/* Export Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${API}/superadmin/export/all-clients`, '_blank')}
                  title={language === "it" ? "Esporta tutti i clienti" : "Export all clients"}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {language === "it" ? "Clienti" : "Clients"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${API}/superadmin/export/all-appointments`, '_blank')}
                  title={language === "it" ? "Esporta tutti gli appuntamenti" : "Export all appointments"}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {language === "it" ? "Appuntamenti" : "Appointments"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${API}/superadmin/export/platform-revenue`, '_blank')}
                  title={language === "it" ? "Esporta report piattaforma" : "Export platform report"}
                >
                  <Download className="w-4 h-4 mr-1" />
                  {language === "it" ? "Report" : "Report"}
                </Button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium mb-1">
                  MRR ({language === "it" ? "Ricavi Mensili Ricorrenti" : "Monthly Recurring Revenue"})
                </p>
                <p className="text-2xl font-mono font-bold text-emerald-600">
                  {formatCurrency(analytics?.mrr || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics?.active_subscriptions || 0} × €34,95
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-700 dark:text-blue-400 font-medium mb-1">
                  ARR ({language === "it" ? "Ricavi Annuali Ricorrenti" : "Annual Recurring Revenue"})
                </p>
                <p className="text-2xl font-mono font-bold text-blue-600">
                  {formatCurrency(analytics?.arr || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  MRR × 12 {language === "it" ? "mesi" : "months"}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">
                  {language === "it" ? "Potenziale da Trial" : "Potential from Trials"}
                </p>
                <p className="text-2xl font-mono font-bold text-amber-600">
                  {formatCurrency(analytics?.pending_revenue || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics?.trial_subscriptions || 0} {language === "it" ? "attività in prova" : "businesses in trial"}
                </p>
              </div>
              
              <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                <p className="text-xs text-violet-700 dark:text-violet-400 font-medium mb-1">
                  {language === "it" ? "ARR Proiettato" : "Projected ARR"}
                </p>
                <p className="text-2xl font-mono font-bold text-violet-600">
                  {formatCurrency(analytics?.projected_arr || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it" ? "Se tutti i trial convertono" : "If all trials convert"}
                </p>
              </div>
            </div>
          </div>

          {/* Upcoming Expirations */}
          {analytics?.upcoming_expirations?.length > 0 && (
            <div className="bg-card rounded-2xl border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-amber-500" />
                {language === "it" ? "Scadenze Imminenti (30 giorni)" : "Upcoming Expirations (30 days)"}
              </h2>
              
              <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
                {analytics.upcoming_expirations.map((exp, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-xl border ${
                      exp.days_remaining <= 7 
                        ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" 
                        : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                    }`}
                  >
                    <p className="font-medium text-sm truncate">{exp.name}</p>
                    <p className={`text-xs font-bold ${exp.days_remaining <= 7 ? "text-red-600" : "text-amber-600"}`}>
                      {exp.days_remaining === 0 
                        ? (language === "it" ? "Scade oggi!" : "Expires today!")
                        : exp.days_remaining === 1
                          ? (language === "it" ? "Scade domani" : "Expires tomorrow")
                          : `${exp.days_remaining} ${language === "it" ? "giorni" : "days"}`
                      }
                    </p>
                    <p className="text-xs text-muted-foreground">{exp.expires_at}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Clients Section with enhanced details */}
          <div className="bg-card rounded-2xl border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              {language === "it" ? "Tutte le Attività" : "All Businesses"}
            </h2>
            
            {analytics?.salons_details?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{language === "it" ? "Attività" : "Business"}</th>
                      <th>Status</th>
                      <th>{language === "it" ? "Piano" : "Plan"}</th>
                      <th>{language === "it" ? "Scadenza" : "Expires"}</th>
                      <th>{language === "it" ? "Giorni Rim." : "Days Left"}</th>
                      <th>{language === "it" ? "Valore/Mese" : "Monthly Value"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.salons_details.map((salon) => (
                      <tr key={salon.tenant_id}>
                        <td>
                          <div>
                            <p className="font-medium">{salon.name}</p>
                            <p className="text-xs text-muted-foreground">/book/{salon.slug}</p>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            {salon.free_forever ? (
                              <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/20 dark:text-pink-400 dark:border-pink-800">
                                <Gift className="w-3 h-3 mr-1" />
                                {language === "it" ? "Gratis" : "Free"}
                              </Badge>
                            ) : (
                              <Badge variant={
                                salon.subscription_status === "active" ? "success" :
                                salon.subscription_status === "trial" ? "warning" : "destructive"
                              }>
                                {salon.subscription_status === "active" ? (language === "it" ? "Attivo" : "Active") :
                                 salon.subscription_status === "trial" ? (language === "it" ? "Prova" : "Trial") :
                                 salon.subscription_status}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="text-sm">
                          {salon.free_forever 
                            ? "-" 
                            : salon.subscription_plan === "annual" 
                              ? (language === "it" ? "Annuale" : "Annual")
                              : (language === "it" ? "Mensile" : "Monthly")
                          }
                        </td>
                        <td className="text-sm">{salon.next_payment || "-"}</td>
                        <td>
                          {salon.free_forever ? (
                            <span className="text-pink-600 text-sm">∞</span>
                          ) : salon.days_until_expiration !== null ? (
                            <span className={`text-sm font-medium ${
                              salon.days_until_expiration <= 7 ? "text-red-600" :
                              salon.days_until_expiration <= 30 ? "text-amber-600" :
                              "text-emerald-600"
                            }`}>
                              {salon.days_until_expiration} {language === "it" ? "gg" : "d"}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td>
                          {salon.free_forever ? (
                            <span className="text-pink-600 text-sm font-medium">€0,00</span>
                          ) : (
                            <span className="text-emerald-600 text-sm font-medium">
                              {formatCurrency(salon.monthly_value || 34.95)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {language === "it" ? "Nessuna attività registrata" : "No businesses registered"}
              </p>
            )}
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Salon Admin Dashboard
  return (
    <AdminLayout>
      <SmartPageOnboardingWizard />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t("welcome_back")}, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">
            {format(new Date(), "EEEE, d MMMM yyyy", { locale: language === "it" ? it : enUS })}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
          <div className="stats-card" data-testid="stat-today-appointments">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                  {t("today_appointments")}
                </p>
                <p className="text-3xl font-mono font-bold mt-2">{analytics?.today_appointments || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-accent" />
              </div>
            </div>
          </div>

          <div className="stats-card" data-testid="stat-total-clients">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                  {t("total_clients")}
                </p>
                <p className="text-3xl font-mono font-bold mt-2">{analytics?.total_clients || 0}</p>
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-3 h-3" />
                  +{analytics?.new_clients_month || 0} {language === "it" ? "questo mese" : "this month"}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </div>

          <div className="stats-card" data-testid="stat-monthly-revenue">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                  {t("monthly_revenue")}
                </p>
                <p className="text-3xl font-mono font-bold mt-2">
                  {formatCurrency(analytics?.monthly_revenue || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </div>

          <div className="stats-card" data-testid="stat-no-show-rate">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">
                  {t("no_show_rate")}
                </p>
                <p className="text-3xl font-mono font-bold mt-2">{analytics?.no_show_rate || 0}%</p>
                {analytics?.no_show_rate > 10 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                    <AlertCircle className="w-3 h-3" />
                    {language === "it" ? "Attenzione alta" : "High attention"}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Today's Appointments */}
          <div className="bg-card rounded-2xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              {t("today_appointments")}
            </h2>
            
            {analytics?.today_appointments_list?.length > 0 ? (
              <div className="space-y-3">
                {analytics.today_appointments_list.slice(0, 5).map((appt) => (
                  <div 
                    key={appt.appointment_id}
                    className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <span className="font-medium text-accent">{appt.time}</span>
                      </div>
                      <div>
                        <p className="font-medium">{appt.client_name}</p>
                        <p className="text-sm text-muted-foreground">{appt.service_name}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      appt.status === "confirmed" ? "badge-success" :
                      appt.status === "pending" ? "badge-warning" :
                      appt.status === "completed" ? "badge-info" : ""
                    }`}>
                      {t(appt.status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {language === "it" ? "Nessun appuntamento oggi" : "No appointments today"}
              </p>
            )}
          </div>

          {/* Popular Services */}
          <div className="bg-card rounded-2xl border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-muted-foreground" />
              {t("popular_services")}
            </h2>
            
            {analytics?.popular_services?.length > 0 ? (
              <div className="space-y-3">
                {analytics.popular_services.map((service, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                        <span className="font-medium text-accent text-sm">#{index + 1}</span>
                      </div>
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <span className="text-muted-foreground">
                      {service.count} {language === "it" ? "prenotazioni" : "bookings"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                {language === "it" ? "Nessun dato disponibile" : "No data available"}
              </p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
